import { useState, type MouseEvent } from 'react';

import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import Avatar from '@mui/material/Avatar';

import { SystemNotificationIcon } from './SystemNotificationIcon';
import AvatarGroup from '@mui/material/AvatarGroup';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useRelativeTime } from '@/lib/dates';
import type { Notification } from '@/lib/schemas/discovery';

import { useMarkNotificationsRead, useNotifications, useUnreadCount } from './useDiscovery';

/**
 * The bell (spec §8.5).
 *
 * The unread count is polled on its own — a small, cheap request — while the
 * list itself is only fetched when the menu is open. Polling the whole list
 * every minute for a badge nobody has clicked would be most of the cost for
 * none of the benefit.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const relativeTime = useRelativeTime();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Sticky: once the list has been fetched it stays in the cache, so reopening
  // the menu shows the previous contents instantly instead of a skeleton.
  const [hasOpened, setHasOpened] = useState(false);
  const navigate = useNavigate();

  const unread = useUnreadCount();
  const notifications = useNotifications(hasOpened);
  const markRead = useMarkNotificationsRead();

  const open = anchor !== null;
  const items = notifications.data?.items ?? [];

  const label =
    unread === 0 ? t('nav.notifications') : t('notifications.bellUnread', { count: unread });

  const openNotification = (notification: Notification): void => {
    setAnchor(null);
    markRead.mutate(notification);
    if (notification.link) navigate(notification.link);
  };

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          size="small"
          onClick={(event: MouseEvent<HTMLElement>) => {
            setHasOpened(true);
            setAnchor(event.currentTarget);
          }}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Badge badgeContent={unread} color="error" max={99}>
            <NotificationsRoundedIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '95vw', mt: 1 } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant="subtitle2">{t('nav.notifications')}</Typography>
          {unread > 0 && (
            <Button
              size="small"
              variant="text"
              startIcon={<DoneAllRoundedIcon fontSize="small" />}
              onClick={() => markRead.mutate(undefined)}
              disabled={markRead.isPending}
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </Stack>
        <Divider />

        {notifications.isPending ? (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} variant="text" height={32} />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {t('notifications.empty')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {items.map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={() => openNotification(notification)}
                sx={{
                  py: 1.25,
                  alignItems: 'flex-start',
                  gap: 1.5,
                  whiteSpace: 'normal',
                  backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                }}
              >
                <AvatarGroup max={3} sx={{ mt: 0.25 }}>
                  {notification.actors.length === 0 ? (
                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'action.selected' }}>
                      <SystemNotificationIcon type={notification.type} />
                    </Avatar>
                  ) : (
                    notification.actors.map((actor) => (
                      <Avatar
                        key={actor.id}
                        src={actor.avatarUrl ?? undefined}
                        alt=""
                        sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                      >
                        {actor.displayName.charAt(0).toUpperCase()}
                      </Avatar>
                    ))
                  )}
                </AvatarGroup>

                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{notification.summary}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {relativeTime(notification.createdAt, true)}
                  </Typography>
                </Stack>

                {!notification.isRead && (
                  <Box
                    aria-hidden
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      mt: 1,
                      ml: 'auto',
                      flexShrink: 0,
                    }}
                  />
                )}
              </MenuItem>
            ))}
          </Box>
        )}
        <Divider />
        <MenuItem
          component={RouterLink}
          to="/notifications"
          onClick={() => setAnchor(null)}
          sx={{ justifyContent: 'center', py: 1.25 }}
        >
          <Typography variant="body2" color="primary">
            {t('notifications.seeEverything')}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
